import { FC, useMemo, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import {
  Add,
  Delete,
  Download,
  Files,
  People,
  Settings,
  Upload,
} from '@factorialco/factorial-one-react/icons/app'
import {
  CreateProjectDocument,
  CreateProjectMutation,
  CreateProjectMutationVariables,
  ProjectManagementProjectEmployeesAssignmentEnum,
  ProjectsDataCollectionDocument,
  ProjectsDataCollectionItemFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'

import { getBillableLabel } from '../../labelTags/BillableTag'
import { getStatusColor, getStatusLabel } from '../../labelTags/StatusTag'
import { ProjectIdWithPermissions } from '../../modals/BulkAssignProjectRolesModal'
import AddProjectWizard from '../../wizards/AddProjectWizard'
import { AssignClientModal } from './modals/AssignClientsModal'
import { AssignEmployeesModal } from './modals/AssignEmployeesModal'
import { AssignRolesModal } from './modals/AssignRolesModal'

const MAX_AVATARS = 4

const MODAL: {
  [key in BulkActionType]: React.ComponentType<{
    ids: number[]
    onCloseModal?: () => void
    initialEmployeesAssignment?: ProjectManagementProjectEmployeesAssignmentEnum | undefined
    projectIdWithPermissions?: ProjectIdWithPermissions[]
  }>
} = {
  ['AssignEmployees']: AssignEmployeesModal,
  ['AssignClient']: AssignClientModal,
  ['AssignRoles']: AssignRolesModal,
}

type BulkActionType = 'AssignEmployees' | 'AssignClient' | 'AssignRoles'

interface ProjectsDataCollectionProps {
  canSeeCosts: boolean
  canCreateProjects: boolean
  canDeleteProjects: boolean
  canManageProjects: boolean

  hasBulkActionsFeature: boolean
  hasClientsFeature: boolean
  hasCostsFeature: boolean
  hasExpensesFeature: boolean
  hasFlexibleTrackingFeature: boolean
  hasImportFeature: boolean
  hasSubprojectsFeature: boolean
  hasTimeTrackingSection: boolean
}

const ProjectsDataCollection: FC<ProjectsDataCollectionProps> = ({
  canSeeCosts,
  canCreateProjects,
  canDeleteProjects,
  canManageProjects,

  hasBulkActionsFeature,
  hasClientsFeature,
  hasCostsFeature,
  hasExpensesFeature,
  hasFlexibleTrackingFeature,
  hasImportFeature,
  hasSubprojectsFeature,
  hasTimeTrackingSection,
}) => {
  const {
    access: { locale },
    employee,
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const canExportData =
    canManageProjects &&
    !!employee &&
    (hasExpensesFeature || hasFlexibleTrackingFeature || hasTimeTrackingSection)

  const showDuplicateProject = canCreateProjects && hasSubprojectsFeature

  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([])
  const [initialEmployeesAssignment, setInitialEmployeesAssignment] = useState<
    ProjectManagementProjectEmployeesAssignmentEnum | undefined
  >(undefined)

  const [projectIdWithPermissions, setProjectIdWithPermissions] = useState<
    ProjectIdWithPermissions[]
  >([])

  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false)

  const [actionType, setActionType] = useState<BulkActionType | null>(null)

  const ActionModal = useMemo(() => (actionType ? MODAL[actionType] : null), [actionType])
  const onCloseModal = () => setActionType(null)

  const source = useGraphqlDataSource({
    document: ProjectsDataCollectionDocument,
    mapVariables: ({ search, sortings }) => ({
      canSeeCosts,
      hasClients: hasClientsFeature,
      hasCosts: hasCostsFeature,
      hasSpendings: hasExpensesFeature,
      nameOrCode: search,
      sortOrder: sortings?.[0],
      maxProjectWorkers: MAX_AVATARS,
    }),
    mapData: (data) => data.projectManagement.projectsConnection.nodes,
    mapPagination: (data) => ({
      total: data.projectManagement.projectsConnection.totalCount ?? 0,
    }),
    itemUrl: ({ id }) => navigation.projects.id.fullPath({ id }),
    itemActions: ({ id, name, _policies }) => {
      const canAssignEmployees = !!_policies?.assignUnassignProjectWorkers.can
      if (!(canAssignEmployees || canDeleteProjects || canManageProjects || showDuplicateProject)) {
        return []
      }

      return compact([
        canAssignEmployees && {
          label: i18n.t('project_management.table.projects_list.menu_links.assign_employees'),
          icon: People,
          onClick: () => {
            track(trackingEvents.projectManagement.projectWorker.assignEmployeesToProjectStart)
            navigate(navigation.projects.id.details.employees.workers.fullPath({ id }))
          },
        },
        showDuplicateProject && {
          label: i18n.t('project_management.table.projects_list.menu_links.duplicate_project'),
          icon: Files,
          onClick: () => {
            navigate(navigation.projects.list.projectId.duplicate.fullPath({ projectId: id }), {
              state: { projectName: name, hasSubprojectsFeature },
            })
          },
        },
        canManageProjects && {
          label: i18n.t('project_management.table.projects_list.menu_links.settings'),
          icon: Settings,
          onClick: () => {
            navigate(navigation.projects.id.settings.fullPath({ id }))
          },
        },
        canDeleteProjects && {
          label: i18n.t('project_management.table.projects_list.menu_links.delete_project'),
          icon: Delete,
          onClick: () => {
            navigate(navigation.projects.list.projectId.delete.fullPath({ projectId: id }), {
              state: { projectName: name },
            })
          },
          critical: true,
        },
      ])
    },
    primaryActions: () =>
      canCreateProjects
        ? {
            label: i18n.t('project_management.add_button'),
            icon: Add,
            onClick: () => {
              setShowCreateProjectModal(true)
            },
          }
        : undefined,
    secondaryActions: () =>
      canExportData || canCreateProjects
        ? compact([
            canCreateProjects && hasImportFeature
              ? {
                  label: i18n.t(
                    'project_management.table.projects_list.table_options.import.title'
                  ),
                  description: i18n.t(
                    'project_management.table.projects_list.table_options.import.description'
                  ),
                  icon: Upload,
                  onClick: () => {
                    track(trackingEvents.projectManagement.importProjects.importProjectsStart)
                    navigate(navigation.projects.list.import.fullPath())
                  },
                }
              : null,
            canExportData
              ? {
                  label: i18n.t(
                    'project_management.table.projects_list.table_options.export.title'
                  ),
                  description: i18n.t(
                    'project_management.table.projects_list.table_options.export.description'
                  ),
                  icon: Download,
                  onClick: () => {
                    track(trackingEvents.projectManagement.projects.downloadProjectsReportStart)
                    navigate(navigation.projects.list.export.fullPath())
                  },
                }
              : null,
          ])
        : undefined,
    selectable: (item) => (hasBulkActionsFeature ? item.id : undefined),
    bulkActions: hasBulkActionsFeature
      ? (selectedItems) => {
          const canAssignEmployees = selectedItems.itemsStatus
            .map((itemStatus) => !!itemStatus.item._policies?.assignUnassignProjectWorkers.can)
            .every((can) => can)

          return {
            primary: compact([
              canAssignEmployees
                ? {
                    id: 'assign-people',
                    label: i18n.t(
                      'project_management.table.projects_list.menu_links.assign_employees'
                    ),
                  }
                : null,
            ]),
            secondary: compact([
              hasClientsFeature
                ? {
                    id: 'assign-client',
                    label: i18n.t(
                      'project_management.table.projects_list.menu_links.assign_client'
                    ),
                  }
                : null,
              {
                id: 'assign-roles',
                label: i18n.t('project_management.table.projects_list.menu_links.assign_roles'),
              },
            ]),
          }
        }
      : undefined,
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('project_management.table.projects_list.headers.name'),
      },
      code: {
        label: i18n.t('project_management.table.projects_list.headers.code'),
      },
      startDate: {
        label: i18n.t('project_management.table.projects_list.headers.startDate'),
      },
      dueDate: {
        label: i18n.t('project_management.table.projects_list.headers.dueDate'),
      },
      status: {
        label: i18n.t('project_management.table.projects_list.headers.status'),
      },
    },
  })

  const onCreateProjectSuccess = (projectId: number) => {
    setShowCreateProjectModal(false)
    navigate(navigation.projects.id.fullPath({ id: projectId }))
  }

  const [createProject] = useMutation(CreateProjectDocument, {
    extractorFn: (data: CreateProjectMutation) =>
      data.projectManagementMutations.createProjectmanagementProject,
  })

  const wrappedCreateProject = (dto: CreateProjectMutationVariables) =>
    createProject({ variables: dto })

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedItems) => {
          setSelectedProjectIds(selectedItems.itemsStatus.map((itemStatus) => itemStatus.item.id))

          switch (action) {
            case 'assign-people':
              setActionType('AssignEmployees')
              if (selectedItems.itemsStatus.length === 1) {
                setInitialEmployeesAssignment(
                  selectedItems.itemsStatus[0]?.item.employeesAssignment
                )
              }
              break
            case 'assign-client':
              setActionType('AssignClient')
              break
            case 'assign-roles':
              setActionType('AssignRoles')
              setProjectIdWithPermissions(
                selectedItems.itemsStatus.map((itemStatus) => ({
                  id: itemStatus.item.id,
                  canAssignOwners: !!itemStatus.item._policies?.assignOwner?.can,
                }))
              )
              break
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.table.projects_list.headers.name'),
                  render: ({ name }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: name,
                  }),
                  sorting: 'name',
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.code'),
                  render: ({ code }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: code ?? undefined,
                  }),
                  sorting: 'code',
                },
                {
                  label: 'People',
                  render: ({ projectworkersConnection }: ProjectsDataCollectionItemFragment) => ({
                    type: 'avatarList',
                    value: {
                      avatarList: projectworkersConnection.nodes.map((node) => ({
                        type: 'person',
                        firstName: node.employee?.firstName ?? '',
                        lastName: node.employee?.lastName ?? '',
                        src: node.employee?.access.avatar
                          ? buildUrl(node.employee.access.avatar)
                          : undefined,
                      })),
                      max: MAX_AVATARS,
                    },
                  }),
                },
                hasClientsFeature
                  ? {
                      label: i18n.t('project_management.table.projects_list.headers.client'),
                      render: ({ client }: ProjectsDataCollectionItemFragment) => ({
                        type: 'text',
                        value: client?.legalName ?? '',
                      }),
                    }
                  : null,
                {
                  label: i18n.t('project_management.table.projects_list.headers.hours'),
                  render: ({ inputedMinutes }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: formatProjectHours(inputedMinutes),
                  }),
                },
                hasCostsFeature
                  ? {
                      label: i18n.t('project_management.table.projects_list.headers.billing'),
                      render: ({ isBillable }: ProjectsDataCollectionItemFragment) => ({
                        type: 'tag',
                        value: {
                          label: getBillableLabel(!!isBillable),
                        },
                      }),
                    }
                  : null,
                hasCostsFeature
                  ? {
                      label: i18n.t('project_management.table.projects_list.headers.cost'),
                      render: ({
                        fixedCostCents,
                        laborCostCents,
                        spendingCostCents,
                        legalEntity,
                      }: ProjectsDataCollectionItemFragment) => {
                        const projectCost =
                          Number(fixedCostCents) +
                          Number(laborCostCents) +
                          Number(spendingCostCents)

                        return {
                          type: 'text',
                          value:
                            fixedCostCents && isCurrency(legalEntity?.currency)
                              ? MoneyWithCurrency({
                                  amount: projectCost,
                                  currency: legalEntity?.currency,
                                  locale,
                                })
                              : '-',
                        }
                      },
                    }
                  : null,
                {
                  label: i18n.t('project_management.table.projects_list.headers.startDate'),
                  render: ({ startDate }: ProjectsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: startDate ? new Date(startDate) : undefined,
                  }),
                  sorting: 'startDate',
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.dueDate'),
                  render: ({ dueDate }: ProjectsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: dueDate ? new Date(dueDate) : undefined,
                  }),
                  sorting: 'dueDate',
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.status'),
                  render: ({ status }: ProjectsDataCollectionItemFragment) => ({
                    type: 'status',
                    value: {
                      label: getStatusLabel(status),
                      status: getStatusColor(status),
                    },
                  }),
                  sorting: 'status',
                },
              ]),
            },
          },
        ]}
      />
      <Outlet
        context={{
          hasExpensesModule: hasExpensesFeature,
          hasFlexibleTracking: hasFlexibleTrackingFeature,
          hasImportProjectsEnabled: hasImportFeature,
        }}
      />
      {ActionModal && (
        <ActionModal
          onCloseModal={onCloseModal}
          ids={selectedProjectIds}
          initialEmployeesAssignment={initialEmployeesAssignment}
          projectIdWithPermissions={projectIdWithPermissions}
        />
      )}
      {showCreateProjectModal && (
        <AddProjectWizard
          onClose={() => setShowCreateProjectModal(false)}
          onSuccess={onCreateProjectSuccess}
          create={wrappedCreateProject}
        />
      )}
    </>
  )
}

export default ProjectsDataCollection
